import { inject, Injectable } from '@angular/core';
import { CreateLimitOrderParams, CreateOrderParams, CreateStopOrderParams, Order, OrderSide, OrderType, Position, Ticker } from '../model/trade';
import { BoardId, MarketId } from '../model/common';
import { parseFloatSafe } from '../func';
import { FormatCurrencyPipe, FormatPricePipe, FormatQuantityPipe } from '../pipe/format.pipe';
import { InstrumentFee } from '../model/trade';
import { InstrumentStorageService } from './instrument-storage.service';
import { UnitWordPipe } from '../pipe/unit-word.pipe';
import { AppTranslateService } from '../service/translate.service';

@Injectable({
  providedIn: 'root'
})
export class OrderHelper {
  private translate = inject(AppTranslateService)

  private instrumentStorage = inject(InstrumentStorageService)

  private activeStatuses: Record<MarketId, string[]> = {
    binance: ['NEW','PARTIALLY_FILLED'],
    bybit: ['New','PartiallyFilled','Untriggered'],
    okx: ['live','partially_filled']
  }

  private binanceUsdmSign = 'x-Jbzjn7gQ' + 'w'  // w - признак того, что ордер от webcscalp
  private bybitSign = 'x-webcscalp'
  private okxSign = 'f39579e04a034eBC' + 'w'  // w - признак того, что ордер от webcscalp

  isNewStatusOrder(order: Order) {
    return ['NEW','New','Untriggered'].includes(order.status)
  }

  isActiveStatusOrder(order: Order) {
    if (this.activeStatuses[order.market]) {
      return this.activeStatuses[order.market].includes(order.status)
    }
    throw new Error(`Unknown market ${order.market}`)
  }

  isClosedStatusOrder(order: Order) {
    return !this.isActiveStatusOrder(order)
  }

  isPartiallyFilled(order: Order) {
    return order.status == 'PartiallyFilled' || order.status == 'PARTIALLY_FILLED' || order.status == 'partially_filled'
  }

  calculateOrderVolume(board: BoardId, price: number | string, quantity: number | string, lotSize: number | string) {
    switch (board) {
      case 'usdm':
      case 'linear':
      case 'swap':
        return parseFloatSafe(price) * parseFloatSafe(quantity) * parseFloatSafe(lotSize)
    }
  }

  calculateOrderQuantity(board: BoardId, price: number | string, volume: number | string, lotSize: number | string) {
    switch (board) {
      case 'usdm':
      case 'linear':
      case 'swap':
        return parseFloatSafe(volume) / parseFloatSafe(price) / parseFloatSafe(lotSize)
    }
  }

  generateClientOrderId(market: MarketId, board?: BoardId) {
    switch (market) {
      case 'binance':
        switch (board) {
          case 'usdm':
            return (this.binanceUsdmSign + crypto.randomUUID().replace(/-/g, '')).substring(0, 32)
          default:
            throw new Error(`Unknown board ${board}`)
        }
      case 'bybit':
        return this.bybitSign + crypto.randomUUID().replace(/-/g, '')
      case 'okx':
        return (this.okxSign + crypto.randomUUID().replace(/-/g, '')).substring(0, 32)
      default:
        throw new Error(`Unknown market ${market}`)
    }
  }

  isOwnOrderId(orderId: string, market?: MarketId): boolean {
    if ((!market || market == 'binance') && orderId.startsWith(this.binanceUsdmSign)) {
      return true
    }
    if ((!market || market == 'bybit') && orderId.startsWith(this.bybitSign)) {
      return true
    }
    if ((!market || market == 'okx') && orderId.startsWith(this.okxSign)) {
      return true
    }
    return false
  }

  isOwnStopOrder(order: Order): boolean {
    return order.isStop === true && this.isOwnOrderId(order.clientOrderId, order.market)
  }

  generateHint(order: Order) {
    const instrument = this.instrumentStorage.get(order.market, order.board, order.symbol)
    const lotSize = instrument?.lotSize ?? 1

    let str = ''
    const price = new FormatPricePipe().transform(order.price)
    const quantity = new FormatQuantityPipe().transform(order.quantity)
    const quantityCoins = new FormatQuantityPipe().transform(parseFloatSafe(order.quantity) * lotSize)
    const volume = new FormatCurrencyPipe().transform(order.volume)
    const unitPipe = new UnitWordPipe()
    const unitLots = unitPipe.transform([this.translate.get('orders.lot'), this.translate.get('orders.lotOC'), this.translate.get('orders.lots')], parseFloatSafe(order.quantity))
    const unitCoins = unitPipe.transform([this.translate.get('orders.coin'), this.translate.get('orders.coinOC'), this.translate.get('orders.coins')], parseFloatSafe(parseFloatSafe(order.quantity) * lotSize))

    if (order.isStop) {
      str += this.translate.get('orders.stop') + ' '
    }
    str += `${this.translate.get('orders.order')}: ${order.side == OrderSide.buy ? this.translate.get('orders.buy') : this.translate.get('orders.sell')}`

    if (order.isStop && parseFloatSafe(order.quantity) == 0) {
      str += ` ${this.translate.get('orders.allPositionOC')} ${order.symbol} ${this.translate.get('orders.for', {price})}`
    }
    else if (order.type == OrderType.market) {
      if (lotSize != 1) {
        str += ` ${quantity} ${unitLots} (${quantityCoins} ${unitCoins})`
      }
      else {
        str += ` ${quantity}`
      }
      str += ` ${order.symbol} ${this.translate.get('orders.forMarketPrice')}`
    }
    else {
      str += ` ${order.symbol} ${this.translate.get('orders.on', {volume: volume})}`
      if (lotSize != 1) {
        str += ` (${quantity} ${unitLots} (${quantityCoins} ${unitCoins}) × ${price})`
      } else {
        str += ` (${quantity} × ${price})`
      }
    }

    str += `, ${order.status}. ID: ${order.orderId}`
    return str
  }

  fillStopOrderForPosition(position: Position, triggerPrice: number) {
    const entryPrice = parseFloatSafe(position.entryPrice)
    const size = parseFloatSafe(position.size)
    if (triggerPrice < entryPrice && size > 0 || triggerPrice > entryPrice && size < 0) {
      const order: CreateStopOrderParams = {
        type: OrderType.market,
        market: position.market,
        board: position.board,
        symbol: position.symbol,
        side: size > 0 ? OrderSide.sell : OrderSide.buy,
        triggerPrice,
        closePosition: true,
      }
      return order
    }
    return undefined
  }

  fillCloseOrderForPosition(position: Position, closePrice: number) {
    const entryPrice = parseFloatSafe(position.entryPrice)
    const size = parseFloatSafe(position.size)
    if (closePrice > entryPrice && size > 0 || closePrice < entryPrice && size < 0) {
      const order: CreateLimitOrderParams = {
        type: OrderType.limit,
        market: position.market,
        board: position.board,
        symbol: position.symbol,
        side: size > 0 ? OrderSide.sell : OrderSide.buy,
        quantity: Math.abs(size),
        price: closePrice,
        closePosition: true,
      }
      return order
    }
    return undefined
  }

  resolveFeeRate(order: CreateOrderParams, feeData: InstrumentFee, ticker: Ticker) {
    if (order.price == undefined) {
      return feeData.taker
    }
    const askPrice   = parseFloatSafe(ticker.askPrice)
    const bidPrice   = parseFloatSafe(ticker.bidPrice)
    const orderPrice = parseFloatSafe(order.price)
    const isBuy  = order.side == OrderSide.buy
    const isSell = order.side == OrderSide.sell

    if (isBuy && orderPrice >= askPrice || isSell && orderPrice <= bidPrice) {
      return feeData.taker
    }
    return feeData.maker
  }

}


/*
Bybit orderStatus

open status

New order has been placed successfully
PartiallyFilled
Untriggered Conditional orders are created

closed status

Rejected
PartiallyFilledCanceled Only spot has this order status
Filled
Cancelled In derivatives, orders with this status may have an executed qty
Triggered instantaneous state for conditional orders from Untriggered to New
Deactivated UTA: Spot tp/sl order, conditional order, OCO order are cancelled before they are triggered
*/
